/**
 * Delivery Settings Page (MVP)
 *
 * Tier-based delivery speed + caps only. Contract-based model.
 * Persists lead_preferences to Supabase agents table on save.
 * Auto-reclaims contracts if the onboarding claim step was missed.
 * Enforces contract lead_types: non-contracted types shown as disabled.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Save, Gauge, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DeliverySettingsCard from "@/components/dashboard/DeliverySettingsCard";
import { useAgent, LeadPreference } from "@/contexts/AgentContext";
import { usePricingRules, PricingRule } from "@/contexts/PricingRulesContext";
import { useToast } from "@/hooks/use-toast";
import { useEnsureContractClaimed } from "@/hooks/useEnsureContractClaimed";
import { supabase } from "@/integrations/supabase/client";
import { bidToVelocityTier, type VelocityTier } from "@/lib/velocityTier";

const DeliverySettings = () => {
  const { state, actions } = useAgent();
  const { state: pricingState } = usePricingRules();
  const { toast } = useToast();
  const contractClaim = useEnsureContractClaimed();
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  // Lead types covered by the agent's active contracts (empty = no restriction)
  const [contractedLeadTypes, setContractedLeadTypes] = useState<string[]>([]);

  const [localPreferences, setLocalPreferences] = useState<LeadPreference[]>(() =>
    JSON.parse(JSON.stringify(state.preferences))
  );

  // Sync local state when context preferences change (e.g. on initial load from DB)
  useEffect(() => {
    setLocalPreferences(JSON.parse(JSON.stringify(state.preferences)));
    setHasChanges(false);
  }, [state.preferences]);

  // Fetch contracted lead types from active contracts
  useEffect(() => {
    const agentEmail = state.profile.email;
    if (!agentEmail) return;

    supabase
      .from("contracts")
      .select("lead_types")
      .eq("agent_email", agentEmail)
      .in("status", ["active", "at_risk"])
      .then(({ data }) => {
        const types = (data || []).flatMap((c: any) =>
          Array.isArray(c.lead_types) ? c.lead_types : []
        );
        setContractedLeadTypes(types);
      });
  }, [state.profile.email]);

  const rulesByLeadType = useMemo(() => {
    const map = new Map<string, PricingRule[]>();
    pricingState.rules.forEach((rule) => {
      const existing = map.get(rule.leadTypeId) || [];
      existing.push(rule);
      map.set(rule.leadTypeId, existing);
    });
    return map;
  }, [pricingState.rules]);

  const toggleActive = useCallback((leadTypeId: string) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, isActive: !pref.isActive } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const updateGlobalBid = useCallback((leadTypeId: string, value: number) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, bidValue: value } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const updateDailyCap = useCallback((leadTypeId: string, value: number) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, dailyCap: value } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const updateMonthlyCap = useCallback((leadTypeId: string, value: number) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, monthlyCap: value } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const tierOrder: VelocityTier[] = ["conservative", "balanced", "aggressive", "maximum"];
  const tierRank = (tier: VelocityTier) => tierOrder.indexOf(tier);

  const getBaselineMinPrice = useCallback((leadTypeId: string) => {
    const rules = rulesByLeadType.get(leadTypeId) || [];
    const allRule = rules.find((r) => r.state === "ALL" && r.active);
    if (allRule) return allRule.basePrice;
    const activeRules = rules.filter((r) => r.active);
    if (activeRules.length === 0) return 20;
    return Math.min(...activeRules.map((r) => r.basePrice));
  }, [rulesByLeadType]);

  const computeTargetTier = useCallback(() => {
    const activePrefs = localPreferences.filter((pref) => pref.isActive);
    if (activePrefs.length === 0) return "balanced" as VelocityTier;
    let bestTier: VelocityTier = "conservative";
    for (const pref of activePrefs) {
      const baseline = getBaselineMinPrice(pref.leadTypeId);
      const tier = bidToVelocityTier(pref.bidValue, baseline);
      if (tierRank(tier) > tierRank(bestTier)) {
        bestTier = tier;
      }
    }
    return bestTier;
  }, [localPreferences, getBaselineMinPrice]);

  const performSave = useCallback(async () => {
    setSaving(true);
    const agentId = state.profile.id;
    const agentEmail = state.profile.email;
    console.log("[DeliverySettings] Saving for agent:", agentId, "email:", agentEmail);

    // 1. Persist lead_preferences to Supabase agents table
    const { error: prefsError } = await supabase
      .from("agents")
      .update({ lead_preferences: localPreferences as unknown as any })
      .eq("id", agentId)
      .select("id");

    if (prefsError) {
      console.error("[DeliverySettings] Failed to persist preferences:", prefsError);
      setSaving(false);
      toast({
        title: "Save failed",
        description: `Could not save preferences: ${prefsError.message}`,
        variant: "destructive",
      });
      return;
    }

    console.log("[DeliverySettings] Preferences saved to agents table");

    // 2. Update context to reflect saved state
    actions.setPreferences(localPreferences);
    setHasChanges(false);

    // 3. Update contract priority_tier
    const targetTier = computeTargetTier();

    // Try by agent_id first
    let { data: activeContracts, error: fetchError } = await supabase
      .from("contracts")
      .select("id")
      .eq("agent_id", agentId)
      .eq("status", "active");

    // If no contracts found by agent_id, try claiming by email then retry
    if (!fetchError && (!activeContracts || activeContracts.length === 0) && agentEmail) {
      console.log("[DeliverySettings] No contracts by agent_id, trying to claim by email:", agentEmail);

      // Attempt to claim unclaimed contracts
      await supabase
        .from("contracts")
        .update({ agent_id: agentId } as any)
        .eq("agent_email", agentEmail)
        .is("agent_id", null);

      // Retry — RLS also allows matching by agent_email
      const retry = await supabase
        .from("contracts")
        .select("id, agent_id, agent_email, status")
        .eq("status", "active");

      activeContracts = retry.data;
      fetchError = retry.error;
      console.log("[DeliverySettings] Contracts after claim attempt:", activeContracts);
    }

    if (fetchError || !activeContracts || activeContracts.length === 0) {
      setSaving(false);
      console.warn("[DeliverySettings] No active contracts found after all attempts");
      toast({
        title: "Preferences saved",
        description: "Delivery preferences saved. No active contract found to update priority — contact your account manager.",
      });
      return;
    }

    // Update priority_tier on all active contracts for this agent
    const contractIds = activeContracts.map((c) => c.id);
    const { error: updateError } = await supabase
      .from("contracts")
      .update({ priority_tier: targetTier })
      .in("id", contractIds);

    setSaving(false);

    if (updateError) {
      console.error("[DeliverySettings] Failed to update priority_tier:", updateError);
      toast({
        title: "Preferences saved",
        description: "Preferences saved, but contract priority update failed.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Settings saved",
      description: `Delivery preferences saved. Priority: ${targetTier}.`,
    });
  }, [localPreferences, actions, toast, computeTargetTier, state.profile.id, state.profile.email]);

  const lifeInsuranceTypes = localPreferences.filter((lt) => lt.vertical === "life_insurance");
  const mortgageTypes = localPreferences.filter((lt) => lt.vertical === "mortgage");

  const activePrefs = localPreferences.filter((lt) => lt.isActive);
  const totalDailyLimit = activePrefs.reduce((sum, lt) => sum + lt.dailyCap, 0);
  const totalMonthlyLimit = activePrefs.reduce((sum, lt) => sum + lt.monthlyCap, 0);

  const hasContract = contractClaim.ready && contractClaim.activeContractIds.length > 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Delivery Settings</h1>
            <p className="text-sm text-muted-foreground">
              Control delivery speed and caps. Pricing is managed by Trusted James.
            </p>
          </div>

          <Button
            onClick={performSave}
            disabled={!hasChanges || saving}
            className="gap-2 w-full sm:w-auto"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        {!contractClaim.ready && (
          <div className="rounded-xl border bg-card p-4 text-center text-sm text-muted-foreground">
            Loading contract information...
          </div>
        )}

        {contractClaim.ready && !hasContract && (
          <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 text-sm text-yellow-700 dark:text-yellow-400">
            No active contract linked to your account. Your delivery preferences will be saved,
            but contract priority won't update until a contract is active.
          </div>
        )}

        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <Gauge className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Overall Limits</div>
              <div className="text-xs text-muted-foreground">
                Daily: {totalDailyLimit} · Monthly: {totalMonthlyLimit}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {lifeInsuranceTypes.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Life Insurance</h2>
              <div className="space-y-3">
                {lifeInsuranceTypes.map((pref) => {
                  const hasContractRestriction = contractedLeadTypes.length > 0;
                  const isContracted = contractedLeadTypes.includes(pref.leadTypeId);
                  const isLocked = hasContractRestriction && !isContracted;
                  if (isLocked) {
                    return (
                      <div key={pref.leadTypeId} className="rounded-xl border border-border/40 bg-muted/20 p-4 opacity-60">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-muted-foreground">{pref.leadTypeName}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                            Not in contract — contact sales to add
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <DeliverySettingsCard
                      key={pref.leadTypeId}
                      preference={pref}
                      pricingRules={rulesByLeadType.get(pref.leadTypeId) || []}
                      onToggleActive={() => toggleActive(pref.leadTypeId)}
                      onUpdateGlobalBid={(v) => updateGlobalBid(pref.leadTypeId, v)}
                      onUpdateDailyCap={(v) => updateDailyCap(pref.leadTypeId, v)}
                      onUpdateMonthlyCap={(v) => updateMonthlyCap(pref.leadTypeId, v)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {mortgageTypes.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Mortgage</h2>
              <div className="space-y-3">
                {mortgageTypes.map((pref) => {
                  const hasContractRestriction = contractedLeadTypes.length > 0;
                  const isContracted = contractedLeadTypes.includes(pref.leadTypeId);
                  const isLocked = hasContractRestriction && !isContracted;
                  if (isLocked) {
                    return (
                      <div key={pref.leadTypeId} className="rounded-xl border border-border/40 bg-muted/20 p-4 opacity-60">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-muted-foreground">{pref.leadTypeName}</span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                            Not in contract — contact sales to add
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <DeliverySettingsCard
                      key={pref.leadTypeId}
                      preference={pref}
                      pricingRules={rulesByLeadType.get(pref.leadTypeId) || []}
                      onToggleActive={() => toggleActive(pref.leadTypeId)}
                      onUpdateGlobalBid={(v) => updateGlobalBid(pref.leadTypeId, v)}
                      onUpdateDailyCap={(v) => updateDailyCap(pref.leadTypeId, v)}
                      onUpdateMonthlyCap={(v) => updateMonthlyCap(pref.leadTypeId, v)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {localPreferences.length === 0 && (
            <div className="rounded-2xl border bg-card p-8 text-center">
              <p className="text-muted-foreground">
                No lead types configured. Complete onboarding to set up your delivery preferences.
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DeliverySettings;
